import React, {useEffect, useState } from "react";
import useScreenSize from "helpers/useScreenSize";
import useDidUpdate from "helpers/useDidUpdate";
import View from "components/05-react/View";
import Pager from "components/05-react/Pager";
import ResultCount from "components/05-react/ResultCount";
import Sort from "components/05-react/Sort";
import Search from "components/05-react/Search";
import Facet from "components/05-react/Facet";
import DateRangeFilter from "components/05-react/DateRangeFilter";
import MobileFilterModal from "components/05-react/MobileFilterModal";
import Spinner from "components/05-react/Spinner";
import ClearAll from "components/05-react/ClearAll";

// Import the Apollo client. See 01-base/graphql/apollo-client.js.
import client from 'kineticGraphql/apollo-client';
// Import global queries for storage entities.
import { search, filter } from 'kineticGraphql/queries.graphql';

function App({
  viewContainerClass,
  viewFacets,
  viewTitle,
  viewItemsPerPage,
  viewDateRange,
  viewFilters,
  viewPreprocessResults,
  viewResultQuery,
  viewSort,
  viewRenderResults
}) {
  // Constants.
  const SORT_OPTIONS = viewSort.map((sort, index) => {
    return { value: index, label: sort.label };
  });

  // State variables.
  const [result, setResult] = useState(null);
  const [page, setPage] = useState(0);
  const [pageChange, setPageChange] = useState(false);
  const [filters, setFilters] = useState(viewFilters ? [viewFilters] : []);
  const [sort, setSort] = useState(viewSort[0]);
  const [searchQuery, setSearchQuery] = useState("");
  const [resetDates, setResetDates] = useState(false);
  const [solrFacets, setSolrFacets] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const { width } = useScreenSize();

  // Helper function to format the facets for the filter.
  const formatFacets = (facets) => {
    const formatted = [];
    const isChecked = (facet, value) => {
      const filter = filters.find((f) => f.tag === facet);
      if (!filter) {
        return false;
      }
      return filter.conditions[0].values.includes(value);
    };
    facets.forEach((facet, i) => {
      if (!facet.values) {
        return;
      }
      let label = viewFacets[facet.field];
      formatted.push({
        heading: label,
        id: facet.field + '-' + i,
        facets: facet.values.map((value, index) => {
          return {
            id: facet.field + '-' + i + '-' + index,
            label: value.value,
            value: facet.field,
            count: value.count,
            checked: isChecked(facet.field, value.value),
          };
        }),
      });
    });
    return formatted;
  };

  // This will break if there's more than 1 option that sorts on the same field.
  const selected = () => viewSort.findIndex((s) => s.field_name === sort.field_name);

  const fetchSearchResults = async (offset) => {
    try {
      const query = {
        query: filter,
        variables: {
          offset: offset,
          limit: viewItemsPerPage,
          query: searchQuery,
          filters,
          sort: {
            field: sort.index_machine_name,
            order: sort.order,
          },
        }
      };
      // Add facets if they exist.
      if (viewFacets !== undefined) {
        query.variables.facets = Object.keys(viewFacets).map((f) => {
          return {field: f, min_count: 1}
        });
        query.query = search;
      }
      const solrData = await client.query(query);
      // Get the Facets, total, and the Node IDs.
      const { count, documents, facets } = solrData.data.allResults;
      if (count === 0) {
        setResult([]);
        setSolrFacets([]);
        setTotalItems(0);
        setLoading(false);
        return;
      }
      const ids = documents.map((doc) => doc.id);
      // Entity query.
      const items = await client.query({
        query: viewResultQuery,
        variables: {
          id: ids,
          limit: viewItemsPerPage,
          sort_field: sort.field_name,
          direction: sort.order,
        },
      });
      // Set state values.
      setTotalItems(count);
      if (facets !== undefined) {
        setSolrFacets(formatFacets(facets));
      } else {
        setSolrFacets([]);
      }
      // Call user function to modify the result set if desired.
      let results = items.data.entityQuery.items;
      if (typeof viewPreprocessResults === 'function') {
        try {
          results = await viewPreprocessResults([...items.data.entityQuery.items]);
        } catch (e) {
          console.error('Error in viewPreprocessResults: ', e);
        }
      }
      setResult(results);
      setLoading(false);
    }
    catch(err) {
      console.error(err.message);
    }
  };

  useEffect(() => {
    const offset = totalItems > 0 ? (page * viewItemsPerPage) % totalItems : 0;
    // console.log('Getting page %d with offset %d', page, offset);
    setLoading(true);
    fetchSearchResults(offset);
  }, [page, pageChange]);

  useDidUpdate(() => {
    // Reset the page to 0 when the filters change.
    // However, if the user is already on page 0, the app wont update since
    // the page state is 0. Using a boolean to force re-fetching results.
    if (page === 0) {
      setPageChange((prevState) => !prevState);
    } else {
      setPage(0);
    }
  }, [sort, filters, searchQuery]);

  useDidUpdate(() => {
    // Change the sort option to Most Relevant when a filter changes.
    // If the user is resetting the filters, change the sort back to the default.
    const sortValue = (filters.length > 1 || searchQuery.length > 0) ? 1 : 0;
    handleSortChange({ target: { value: sortValue } });
  }, [filters, searchQuery]);

  // Reset the date range when user clears all filters.
  useEffect(() => {
    if (viewDateRange) {
      setResetDates(false);
    }
  }, [resetDates])


  function handlePageClick(event) {
    // const newOffset = (event.selected * viewItemsPerPage) % totalItems;
    // console.log(`User requested page number ${event.selected}, which is offset ${newOffset}`);
    setPage(event.selected);
  }

  function handleSearchQuery({ query }) {
    // console.log(`User requested to search for ${query}`);
    setSearchQuery(query);
  }

  function handleSortChange(event) {
    const { value } = event.target;
    // console.log(`User requested to sort by ${value}`);
    setSort(viewSort[value]);
  }

  function handleDateRangeChange(event) {
    const { startDate, startField, endDate, endField } = event;
    setFilters((prevFilters) => {
      // Remove the previous Date filters.
      const newFilters = prevFilters.filter((filter) => filter.tag !== "date");
      // Check if the start and end date are set.
      if (!startDate || !endDate) {
        return [...newFilters];
      }
      // Divide by 1000 to get the Unix timestamp.
      const startValue = new Date(startDate).valueOf() / 1000;
      // Add 86399000 to the end date to get the end of the day.
      const endValue = (new Date(endDate).valueOf() + 86399000) / 1000;
      // Add the start and end date.
      newFilters.push({tag: "date", conjunction: "AND", conditions: [
        { field: startField, values:[startValue.toString()], operator:">="},
        { field: endField, values:[endValue.toString()], operator:"<="}
      ]});
      return [...newFilters];
    });
  }

  function handleFacetChange(event) {
    const { value, checked } = event.target;
    const { filter } = event.target.dataset;
    // console.log(`User requested to filter by ${filter} with value ${value}. Input checked: ${checked}`);
    setFilters((prevFilters) => {
      // Find the current filter.
      const filterIndex = prevFilters.findIndex((_f) => _f.tag === filter);
      if (filterIndex > -1) {
        // Check if the value is already in the conditions.
        const currentCondition = prevFilters[filterIndex].conditions[0];
        if (!checked) {
          // Check if this is the only value in the conditions.
          if (currentCondition.values.length === 1) {
            // Remove the whole filter.
            prevFilters.splice(filterIndex, 1);
            return [...prevFilters];
          }
          // Remove this value from the conditions.
          const valueIndex = currentCondition.values.indexOf(value);
          prevFilters[filterIndex].conditions[0].values.splice(valueIndex, 1);
        } else {
          prevFilters[filterIndex].conditions[0].values.push(value);
        }
      } else {
        // Add the filter as a new condition group.
        prevFilters.push({tag: filter, conjunction: "OR", conditions: [{ field: filter, values: [value]}]});
      }
      return [...prevFilters];
    });
  }

  // Reset the state to the initial values.
  function handleClearAll() {
    setSearchQuery("");
    setResetDates(true);
    setFilters(viewFilters ? [viewFilters] : []);
  }

  // Render the filters. On mobile, they are in a modal.
  function renderFilters() {
    if (width >= window.kinetic.breakpoints.all.md.value) {
      return(
        <>
          <Search value={searchQuery} hideLabel={false} label="Search" onSearchQuery={handleSearchQuery} />
          <h4 className="filter-label mt-3"> Filters </h4>
          { viewDateRange && <DateRangeFilter startField={viewDateRange.startField} endField={viewDateRange.endField} resetDates={resetDates} onDateRangeChange={handleDateRangeChange} /> }
          { solrFacets.map((facet, index) => <Facet expanded={index === 0} key={`${facet.id}`} heading={facet.heading} facets={facet.facets} onFacetChange={handleFacetChange} /> )}
        </>
      );
    }
    return (
      <>
        <div className="mobile-filter-wrapper">
          <Search value={searchQuery} hideLabel={true} label="Search" onSearchQuery={handleSearchQuery} />
          <MobileFilterModal handleClearAll={handleClearAll}>
            { viewDateRange && <DateRangeFilter startField={viewDateRange.startField} endField={viewDateRange.endField} resetDates={resetDates} onDateRangeChange={handleDateRangeChange} /> }
            { solrFacets.map((facet, index) => <Facet expanded={index === 0} key={`${facet.id}`} heading={facet.heading} facets={facet.facets} onFacetChange={handleFacetChange} /> )}
          </MobileFilterModal>
        </div>
        { viewTitle !== undefined && <h2> { viewTitle } </h2>}
      </>
    );
  }

  if (!result) {
    return <div style={{ height : '300px' }} className="relative"><Spinner position="absolute" /></div>;
  }

  // By default, the filters should be length 1 for the defaults.
  const showClearAll = !loading && (filters.length > 1 || searchQuery.length > 0);

  return (
    <View containerClass={viewContainerClass} loading={loading}>
      <View.header>
        <div>
          <ResultCount totalItems={totalItems} page={page} itemsPerPage={viewItemsPerPage} />
          { showClearAll && <ClearAll handleClearAll={handleClearAll} /> }
        </div>
        <Sort sortOptions={SORT_OPTIONS} selected={selected()} handleSortChange={handleSortChange} />
      </View.header>
      <View.filters>
        {renderFilters()}
      </View.filters>
      <View.rows>
        {viewRenderResults(result)}
      </View.rows>
      <View.footer>
        <Pager itemsPerPage={viewItemsPerPage} totalItems={totalItems} activePage={page} handlePageClick={handlePageClick}/>
      </View.footer>
    </View>
  );
}

export default App;
